import { z } from 'zod';

export const deliveryLineSchema = z.object({
  lineId: z.string(),
  poLineId: z.string(),
  itemId: z.string(),
  itemName: z.string(),
  sku: z.string(),
  orderedQuantity: z.number(),
  receivedQuantity: z.number().optional(),
  unit: z.string(),
  status: z.enum(['pending', 'received', 'partial', 'rejected']),
  qualityStatus: z.enum(['pending', 'passed', 'failed', 'hold']).optional(),
  temperature: z.number().optional(),
  lotNumber: z.string().optional(),
  expiryDate: z.string().optional(),
  notes: z.string().optional(),
});

export const deliverySchema = z.object({
  deliveryId: z.string(),
  poId: z.string(),
  poNumber: z.string(),
  supplierId: z.string(),
  supplierName: z.string(),
  siteId: z.string(),
  siteName: z.string(),
  status: z.enum(['scheduled', 'in-progress', 'completed', 'partial', 'rejected']),
  scheduledDate: z.string(),
  actualArrivalDate: z.string().optional(),
  completedDate: z.string().optional(),
  lines: z.array(deliveryLineSchema),
  totalLines: z.number(),
  receivedLines: z.number(),
  receivedBy: z.string().optional(),
  receivedByName: z.string().optional(),
  notes: z.string().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type DeliveryLine = z.infer<typeof deliveryLineSchema>;
export type Delivery = z.infer<typeof deliverySchema>;
export type DeliveryStatus = Delivery['status'];
export type DeliveryLineStatus = DeliveryLine['status'];
export type QualityStatus = NonNullable<DeliveryLine['qualityStatus']>;

export const deliveriesResponseSchema = z.object({
  data: z.array(deliverySchema),
  pagination: z.object({
    nextToken: z.string().nullable(),
    totalCount: z.number() 
  }),
  meta: z.object({ siteId: z.string() }),
});

export type DeliveriesResponse = z.infer<typeof deliveriesResponseSchema>;

export const DELIVERY_STATUS_LABELS: Record<DeliveryStatus, string> = {
  scheduled: 'Scheduled',
  'in-progress': 'In Progress',
  completed: 'Completed',
  partial: 'Partial',
  rejected: 'Rejected',
};

export const DELIVERY_STATUS_COLORS: Record<DeliveryStatus, string> = {
  scheduled: 'bg-blue-100 text-blue-800',
  'in-progress': 'bg-amber-100 text-amber-800',
  completed: 'bg-green-100 text-green-800',
  partial: 'bg-orange-100 text-orange-800',
  rejected: 'bg-red-100 text-red-800',
};

export const QUALITY_STATUS_COLORS: Record<QualityStatus, string> = {
  pending: 'bg-gray-100 text-gray-800',
  passed: 'bg-green-100 text-green-800',
  failed: 'bg-red-100 text-red-800',
  hold: 'bg-amber-100 text-amber-800',
};
